import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  PriceChange as PriceChangeIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  Analytics as AnalyticsIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import price list image
import priceListImage from '../assests/price-list-page/price-list.png';

const PriceListPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #16a34a, #22c55e)',
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #22c55e, #4ade80)',
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #15803d, #16a34a)',
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Master Your Pricing with{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#16a34a',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Smart Price Lists
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Create dynamic price lists, manage wholesale pricing, and boost your sales performance!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#16a34a',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#15803d'
              }
            }}
          >
            Start Pricing Today
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Instant setup - Begin optimizing your pricing strategy immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{ 
      width: '100%', 
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -20 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'Dynamic Pricing',
          'Wholesale Management',
          'Customer Pricing',
          'Price List Creation',
          'Margin Analysis',
          'Volume Discounts',
          'Competitive Pricing',
          'Profit Optimization',
          'Customer Segmentation',
          'Revenue Growth',
          'Price Tracking',
          'Pricing Strategy',
          'Sales Analytics',
          'Price Comparison',
          'Bulk Pricing',
          'Customer Tiers',
          'Dynamic Pricing',
          'Wholesale Management',
          'Customer Pricing',
          'Price List Creation',
          'Margin Analysis',
          'Volume Discounts',
          'Competitive Pricing',
          'Revenue Growth',
          'Price Tracking',
          'Pricing Strategy',
          'Sales Analytics',
          'Price Comparison',
          'Bulk Pricing',
          'Customer Tiers'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#16a34a', 0.15)}`,
                bgcolor: alpha('#16a34a', 0.05),
                borderColor: alpha('#16a34a', 0.3),
                color: '#16a34a'
              }
            }}
          />
        ))}
      </Box>
      
      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <PriceChangeIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Dynamic Price Lists',
        description: 'Create flexible price lists with multiple pricing strategies, customer tiers, and automatic calculations for maximum profitability.',
        color: '#16a34a',
        items: ['Multiple pricing tiers', 'Automatic calculations', 'Customer segmentation', 'Bulk pricing options']
      },
      {
        icon: <TrendingUpIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Sales Optimization',
        description: 'Boost your revenue with intelligent pricing strategies, margin analysis, and competitive pricing tools for better sales performance.',
        color: '#15803d',
        items: ['Margin analysis', 'Competitive pricing', 'Revenue optimization', 'Sales analytics']
      },
      {
        icon: <StoreIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Wholesale Management',
        description: 'Streamline wholesale operations with specialized pricing, volume discounts, and customer-specific pricing structures.',
        color: '#7c3aed',
        items: ['Volume discounts', 'Wholesale pricing', 'Customer-specific rates', 'Bulk order management']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: '#16a34a' }}>Perfect Pricing</Box>
          </Typography>
        </Box>
        
        {mainFeatures.map((feature, index) => (
          <Box 
            key={index}
            sx={{ 
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{ 
              maxWidth: '1300px', 
              mx: 'auto', 
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: { xs: 6, md: 8 }, 
                          height: { xs: 6, md: 8 }, 
                          borderRadius: '50%', 
                          backgroundColor: '#ffffff', 
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <LocalOfferIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Price Tiers
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Multiple customer pricing levels
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <MoneyIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Smart Calculations
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Automated pricing computations
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 1 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <AssessmentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Sales Analytics
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Track performance metrics
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <TimelineIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Margin Tracking
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Monitor profit margins in real-time
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 2 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <InventoryIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Volume Pricing
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Bulk order discount structures
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <GroupsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Customer Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Individual customer pricing
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* Main Image Section - Price List View */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<ReceiptIcon />}
              label="Price Management"
              variant="outlined"
              sx={{ 
                borderColor: '#16a34a',
                color: '#16a34a',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#16a34a'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Manage Your{' '}
            <Box component="span" sx={{ color: '#16a34a' }}>Complete Pricing Strategy</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            From wholesale to retail, control every aspect of your pricing with precision and flexibility
          </Typography>
        </Box>

        {/* Large Image Display */}
        <Box sx={{ 
          maxWidth: '1800px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={priceListImage}
              alt="Price List Management"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>

        {/* Feature Grid Below Image */}
        <Box sx={{ 
          maxWidth: '1200px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: ShoppingCartIcon, 
              title: 'Easy Price Updates',
              desc: 'Update prices across all products and customer segments with just a few clicks'
            },
            { 
              icon: AnalyticsIcon, 
              title: 'Profit Analysis',
              desc: 'Real-time margin calculations and profitability insights for better decisions'
            },
            { 
              icon: AccountBalanceIcon, 
              title: 'Customer Tiers',
              desc: 'Create different pricing levels for various customer categories'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(5, 150, 105, 0.12)',
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#16a34a', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: '#16a34a' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: 4, md: 8 } }}>
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: TrendingUpIcon, number: '35%', label: 'Revenue Increase', color: '#16a34a' },
            { icon: MoneyIcon, number: '98%', label: 'Pricing Accuracy', color: '#15803d' },
            { icon: TimelineIcon, number: '60%', label: 'Time Savings', color: '#7c3aed' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{ 
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{ 
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#16a34a',
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to optimize your pricing strategy?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#bbf7d0'
          }}
        >
          Join businesses that have increased their revenue and streamlined their pricing with our intelligent price list management system.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: '#16a34a',
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #16a34a',
            '&:hover': {
              backgroundColor: '#16a34a',
              color: '#ffffff'
            }
          }}
        >
          Start Optimizing Prices Today
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default PriceListPage;